import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Droplet, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message || "Password reset");
      navigate("/login");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "linear-gradient(150deg, #451a03, #78350f 55%, #b45309)" }}>
        <Link to="/" className="flex items-center gap-2 text-amber-100 relative z-10">
          <Droplet className="w-7 h-7" style={{ color: "#F59E0B" }} fill="#F59E0B" />
          <span className="font-display text-3xl font-bold">Drops of Honey</span>
        </Link>
        <div className="relative z-10 text-amber-50">
          <h2 className="font-display text-5xl font-bold leading-tight">Set a fresh password.</h2>
          <p className="mt-4 text-amber-100/80 max-w-md">Pick something strong and memorable — then you're back to cataloging your grail pieces.</p>
        </div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.35), transparent 70%)" }} />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm float-up">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <Droplet className="w-7 h-7 brand-text" fill="currentColor" />
            <span className="font-display text-2xl font-bold">Drops of Honey</span>
          </div>

          {!token ? (
            <div data-testid="reset-invalid-message">
              <h1 className="font-display text-4xl font-bold">Invalid link</h1>
              <p className="text-muted-page mt-2">This reset link is missing or malformed. Please request a new one.</p>
              <Link to="/forgot-password" className="inline-block mt-6 px-5 py-2.5 rounded-full font-semibold text-white" style={{ background: "var(--brand)" }}>Request a new link</Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-password-form">
              <h1 className="font-display text-4xl font-bold">New password</h1>
              <p className="text-muted-page mt-1 mb-6">Choose a new password for your account.</p>

              {error && <div data-testid="reset-error" className="mb-4 text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}

              <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">New password</label>
              <input data-testid="reset-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full surface border rounded-lg px-3 py-2.5 mb-4 focus:outline-none focus:ring-2 focus:ring-amber-400" />

              <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Confirm password</label>
              <input data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full surface border rounded-lg px-3 py-2.5 mb-6 focus:outline-none focus:ring-2 focus:ring-amber-400" />

              <button data-testid="reset-submit-btn" disabled={loading} className="w-full py-3 rounded-full font-semibold disabled:opacity-60" style={{ background: "var(--brand)", color: "#fff" }}>
                {loading ? "Saving…" : "Reset password"}
              </button>
              <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-page hover:brand-text mt-4">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
